import auth from '@/auth/auth';
import {
    CreateMultiLinkDocument,
    DeleteSeriesDocument,
    GetSeriesDocument,
    SetSingleEventDocument,
    UpdateEventSeriesDocument,
    UpdateEventSeriesMultiLinkDocument,
    UpdateEventSeriesMutationVariables,
    UpdateEventSeriesStatusDocument,
} from '@/generated/graphql';
import fluent from '@/plugins/fluent';
import { thumbnailUrl } from '@/utils/file';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed, Ref, toValue } from 'vue';
import { useRouter } from 'vue-router';
import { EventSeriesStatus } from '../types';

export function useEventSeries(seriesId: Ref<string>) {
    const isAdmin = auth.isAdmin();
    const router = useRouter();

    const { result, loading } = useQuery(
        GetSeriesDocument,
        () => ({ id: toValue(seriesId) }),
        () => ({
            enabled: !!toValue(seriesId),
        }),
    );

    const series = computed(() => result.value?.eventSeries);

    const createEventDisabledExplanation = computed(() => {
        if (!isAdmin) {
            return fluent.$t('no-permission-to-create');
        }
        if (!series.value) {
            return fluent.$t('no-loaded-series');
        }
        if (series.value.singleEvent && series.value.events && series.value.events.nodes.length >= 1) {
            return fluent.$t('single-event-limit-reached');
        }
        return undefined;
    });

    const convertDisabledExplanation = computed(() => {
        if (!isAdmin) {
            return fluent.$t('no-permission-to-edit');
        }
        if (!series.value) {
            return fluent.$t('no-loaded-series');
        }
        if (!series.value.singleEvent && series.value.events && series.value.events.nodes.length > 1) {
            return fluent.$t('series-contains-multiple-events');
        }
        return undefined;
    });

    const deleteDisabledExplanation = computed(() => {
        if (!isAdmin) return fluent.$t('no-permission-to-edit');
        if (!series.value) {
            return fluent.$t('no-loaded-series');
        }
        if (series.value.events && series.value.events.nodes.length > 0) return fluent.$t('series-has-events');
        return undefined;
    });

    const createEventButtonLabel = computed(() =>
        series.value?.singleEvent ? fluent.$t('single-event-create') : fluent.$t('event-create'),
    );

    const convertButtonLabel = computed(() =>
        series.value?.singleEvent ? fluent.$t('allow-multiple-events') : fluent.$t('limit-to-single-event'),
    );

    const iconUrl = computed(() => thumbnailUrl(series.value?.icon, 'url512'));

    const updateSeriesMutation = useMutation(UpdateEventSeriesDocument);
    async function updateEventSeries(data: UpdateEventSeriesMutationVariables) {
        const result = await updateSeriesMutation.mutate(data);
        if (result?.errors) {
            throw Error(`Error on update event series: ${result.errors}`);
        }
    }

    const updateStatusMutation = useMutation(UpdateEventSeriesStatusDocument);
    async function updateEventSeriesStatus(status: EventSeriesStatus) {
        if (!series.value) return;
        if (status === series.value.status) return;
        const seriesId = series.value.id;
        const response = await updateStatusMutation.mutate({ id: seriesId, status });
        if (response?.errors) throw new Error(`Could not update event series status: ${response.errors}`);
    }

    const updateSeriesMultiLinksMutation = useMutation(UpdateEventSeriesMultiLinkDocument);
    async function updateEventSeriesMultiLinkId(fieldName: string, multiLinkId: string) {
        if (!series.value) return;
        const response = await updateSeriesMultiLinksMutation.mutate({ id: series.value.id, [fieldName]: multiLinkId });
        if (response?.errors) {
            throw new Error(`Could not update event series multilink ${fieldName}: ${response.errors}`);
        }
    }

    const createMultiLinkMutation = useMutation(CreateMultiLinkDocument);
    async function createEventSeriesMultiLink(fieldName: string) {
        if (!series.value) return;
        const name = `${series.value.id}-${fieldName}`;
        const response = await createMultiLinkMutation.mutate({ name });
        if (response?.errors) {
            throw new Error(`Could not create Multilink for event series: ${response.errors}`);
        }
        if (response?.data) {
            const multiLinkId = response.data.createMultilink?.multilink?.id;
            if (multiLinkId) {
                await updateEventSeriesMultiLinkId(`${fieldName}Id`, multiLinkId);
                return multiLinkId;
            }
        }
    }

    const setSingleEventMutation = useMutation(SetSingleEventDocument);
    const toggleSingleEvent = async () => {
        if (!series.value) return;
        const response = await setSingleEventMutation.mutate({
            id: toValue(seriesId),
            singleEvent: !series.value.singleEvent,
        });
        if (response?.errors) throw new Error(`Could not update event series singleEvent value: ${response.errors}`);
    };

    const deleteMutation = useMutation(DeleteSeriesDocument, {
        refetchQueries: ['GetEvents'],
    });
    async function deleteEventSeries() {
        if (!series.value) return;
        if (!isAdmin) return;

        if (!confirm(fluent.$t('confirm-delete-series', { series: series.value.nameShort }))) return;

        const response = await deleteMutation.mutate({ seriesId: toValue(seriesId) });
        if (response?.errors) {
            throw Error(`Could not delete event series: ${response.errors}`);
        }
        if (response?.data) {
            router.replace({ name: 'events' });
        }
    }

    return {
        series,
        loading,
        createEventDisabledExplanation,
        convertDisabledExplanation,
        deleteDisabledExplanation,
        createEventButtonLabel,
        convertButtonLabel,
        iconUrl,
        updateEventSeriesStatusError: updateStatusMutation.error,
        updateEventSeriesStatusLoading: updateStatusMutation.loading,
        updateEventSeries,
        updateEventSeriesStatus,
        createEventSeriesMultiLink,
        updateEventSeriesMultiLinkId,
        toggleSingleEvent,
        deleteEventSeries,
    };
}
